<template>
  <v-app
    id="report"
    :class="{ 'sample-report': isSampleReport, rrb: customView }"
  >
    <v-container v-if="report.id">
      <v-layout v-if="!loadingPdf" wrap justify-center>
        <v-flex
          v-if="!customView"
          xs8
          md4
          lg2-5
          class="sidebar-container"
          :class="{ display: displaySidebar, remove: displaySidebar === false }"
        >
          <sidebar-menu
            v-if="loaded"
            :firestore="firestore"
            @change="displaySidebar = false"
            @publish="setReportPublish"
            @submit="submitReport = true"
          />
        </v-flex>
        <v-flex
          xs12
          id="scrollable"
          class="main-container"
          :class="{ 'md8 lg9-5': !customView, md12: customView }"
          @click.capture="displaySidebar = false"
        >
          <v-layout wrap justify-center class="main">
            <transition name="slide-down">
              <v-flex v-if="rrb" xs12>
                <r-r-b />
              </v-flex>
              <v-flex v-else-if="rib" xs12>
                <r-i-b />
              </v-flex>
            </transition>
            <template v-if="!customView">
              <v-flex id="header" xs12>
                <report-header v-if="loaded" />
                <div v-else class="report-header-placeholder">&nbsp;</div>
              </v-flex>
              <v-flex
                v-if="loaded"
                xs12
                id="options-row"
                class="options-row"
                :class="{ scroll: scrollHeader }"
              >
                <div class="options sidebar-option">
                  <div @click="displaySidebar = true" class="option">
                    <div class="fa fa-reorder" />
                  </div>
                </div>
                <div class="options">
                  <div
                    class="option"
                    @click="setActiveFilter('full')"
                    :class="{ active: displayAll }"
                  >
                    <div class="fa fa-file-text-o" />
                    <span>Full Report</span>
                  </div>
                  <template v-if="loaded">
                    <template v-if="reinspection">
                      <div
                        class="option"
                        @click="setActiveFilter('resolved')"
                        :class="{ active: activeFilter === 'resolved' }"
                      >
                        <div class="fa fa-thumbs-o-up" />
                        <span>{{
                          report.attributes.settings.reinspect_category_good
                        }}</span>
                      </div>
                      <div
                        v-if="
                          report.attributes.settings.reinspect_categories === 3
                        "
                        class="option"
                        @click="setActiveFilter('unsatisfactory')"
                        :class="{ active: activeFilter === 'unsatisfactory' }"
                      >
                        <div class="fa fa-hand-paper-o" />
                        <span>{{
                          report.attributes.settings.reinspect_category_partial
                        }}</span>
                      </div>
                      <div
                        v-if="
                          report.attributes.settings.reinspect_categories !== 1
                        "
                        class="option"
                        @click="setActiveFilter('notResolved')"
                        :class="{ active: activeFilter === 'notResolved' }"
                      >
                        <div class="fa fa-thumbs-o-down" />
                        <span>{{
                          report.attributes.settings.reinspect_category_bad
                        }}</span>
                      </div>
                    </template>
                    <template v-else>
                      <div
                        v-if="summaryCategories.length > 0"
                        class="option"
                        @click="setActiveFilter('summary')"
                        :class="{ active: activeFilter === 'summary' }"
                      >
                        <div class="fa fa-list-ul" />
                        <span>Summary</span>
                      </div>
                      <div
                        v-if="report.attributes.settings.categories !== 1"
                        class="option"
                        @click="setActiveFilter('hazards')"
                        :class="{ active: activeFilter === 'hazards' }"
                      >
                        <div class="fa fa-warning" />
                        <span>{{
                          report.attributes.settings.category_high
                        }}</span>
                      </div>
                    </template>
                  </template>
                </div>

                <div class="options">
                  <v-menu
                    v-if="!pdfsBeingRefreshed"
                    v-model="showPdfOptions"
                    absolute
                    offset-y
                    min-width="10em"
                  >
                    <div slot="activator" class="pdf-btn option">
                      <div class="fa fa-file-pdf-o" />
                      <span>PDF</span>
                      <div class="fa fa-caret-down" />
                    </div>

                    <v-list>
                      <v-list-tile
                        v-if="!reinspection"
                        :href="summaryUrl"
                        target="_blank"
                        class="report-option"
                      >
                        <v-list-tile-title>
                          <div class="icon-container">
                            <div class="fa fa-list" />
                          </div>
                          Summary
                        </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile
                        :href="pdfUrl"
                        target="_blank"
                        class="report-option"
                      >
                        <v-list-tile-title>
                          <div class="icon-container">
                            <div class="fa fa-file-pdf-o" />
                          </div>
                          Full Report
                        </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile
                        v-if="canRefresh"
                        @click="refreshPdfs"
                        class="report-option"
                      >
                        <v-list-tile-title>
                          <div class="icon-container">
                            <div class="fa fa-refresh" />
                          </div>
                          Refresh PDFs
                        </v-list-tile-title>
                      </v-list-tile>
                    </v-list>
                  </v-menu>
                  <v-tooltip v-else left content-class="info-tooltip">
                    <div slot="activator" class="pdf-btn refreshing">
                      <div class="fa fa-refresh rotating" />
                      <span>PDF</span>
                    </div>
                    Your PDFs are currently being updated with the latest
                    information. They should be ready within a minute or two.
                  </v-tooltip>
                </div>
              </v-flex>
            </template>

            <v-flex
              v-if="loaded"
              id="report-body"
              class="report-body"
              :class="{ xs11: !customView, xs10: customView }"
              data-cy="report_body"
            >
              <div v-if="activeFilter === 'summary'" class="summary-text">
                <p v-html="report.attributes.settings.summary_text" />
              </div>

              <stats v-if="displaySummary" id="summary" />

              <div
                v-if="displayReportText"
                v-html="report.attributes.settings.full_report_text"
                class="report-text"
              />

              <template v-for="numberedSection in numberedSections">
                <div
                  v-if="
                    displayAll ||
                    defectCounts[activeFilter][numberedSection.section.id]
                      .length > 0
                  "
                  :key="numberedSection.number"
                  :id="`section-${numberedSection.section.id}`"
                >
                  <div class="section-header">
                    <span class="section-name">
                      <span :class="numberedSection.section.attributes.icon" />
                      {{ numberedSection.number }} -
                      {{ numberedSection.section.attributes.name }}
                    </span>

                    <template
                      v-if="
                        !firestore &&
                        canEdit &&
                        numberedSection.observations.length > 0
                      "
                    >
                      <a
                        class="btn btn-blank btn-flat btn-small"
                        :href="`/report/${
                          report.id
                        }/editor/#/item/${firstItemId(
                          numberedSection.section.id
                        )}`"
                      >
                        <span class="fa fa-pencil" />
                        Edit Section
                      </a>
                    </template>
                  </div>

                  <v-flex
                    v-if="displayAll && !customView && !reinspection"
                    xs12
                  >
                    <section-tabs
                      :sectionNumber="numberedSection.number"
                      :section="numberedSection.section"
                      :observations="numberedSection.observations"
                    />
                    <div
                      v-if="
                        report.attributes.settings.use_ratings &&
                        report.attributes.settings.display_report_grids &&
                        !numberedSection.section.attributes.is_overview
                      "
                      class="small ratings-legend"
                    >
                      <span
                        v-for="(abbr, i) in report.attributes
                          .ratings_abbreviations"
                        :key="i"
                      >
                        {{ abbr }} = {{ report.attributes.ratings_names[i] }}
                      </span>
                    </div>
                  </v-flex>

                  <v-layout wrap class="observations">
                    <template v-if="numberedSection.observationsLoaded">
                      <template
                        v-for="obs in defectObservations(numberedSection)"
                      >
                        <v-flex
                          v-if="
                            displayAll || displayable(obs.attributes.category)
                          "
                          xs12
                          sm6
                          md6
                          lg4
                          class="observation-container"
                          :key="`${obs.attributes.item_id}-${obs.id}`"
                        >
                          <defect-options
                            v-if="customView"
                            :observationId="obs.id"
                            :label="obs.number"
                          />
                          <defect-card
                            :label="
                              reinspection
                                ? obsLabel(obs.attributes.source_id)
                                : obs.number
                            "
                            :observation="obs"
                            :numbered-sections="
                              numberedSectionsWithObservations
                            "
                          />
                        </v-flex>
                      </template>
                    </template>
                    <v-flex layout wrap v-else class="loading-wrapper">
                      <div class="swoosh" />
                      <v-flex
                        xs12
                        sm6
                        md6
                        lg4
                        class="fake observation-container"
                      >
                        <div class="defect-card">
                          <div class="defect-header"
                            >{{ numberedSection.number }}.1.1 Foundation,
                            Basement & Crawlspaces</div
                          >
                          <div class="defect-body">
                            <div class="name">Heaving/Settling</div>
                            <div class="text"
                              >The floor slab shows movement/settling due to
                              soil movement. This can compromise the structural
                              integrity of the home. Recommend a qualified
                              structural engineer evaluate and advise on how to
                              remedy.</div
                            >
                          </div>
                        </div>
                      </v-flex>
                      <v-flex
                        hidden-xs-only
                        sm6
                        md6
                        lg4
                        class="fake observation-container"
                      >
                        <div class="defect-card">
                          <div class="defect-header"
                            >{{ numberedSection.number }}.1.2 Floor
                            Structure</div
                          >
                          <div class="defect-body">
                            <div class="name"
                              >Evidence of Structural Damage</div
                            >
                            <div class="text"
                              >Structural damage was observed in the underlying
                              floor structure. Recommend a structural engineer
                              evaluate.</div
                            >
                          </div>
                        </div>
                      </v-flex>
                      <v-flex
                        hidden-md-and-down
                        lg4
                        class="fake observation-container"
                      >
                        <div class="defect-card">
                          <div class="defect-header"
                            >{{ numberedSection.number }}.1.3 Roof Structure &
                            Attic</div
                          >
                          <div class="defect-body">
                            <div class="name">Safety Hazard</div>
                            <div class="text"
                              >This area was unsafe to inspect. If you are
                              interested in having this area inspected, please
                              contact me about a follow-up inspection.</div
                            >
                          </div>
                        </div>
                      </v-flex>
                    </v-flex>
                  </v-layout>
                </div>
              </template>
              <report-recommended-contractors
                v-if="loaded && canRecommendContractors"
              />
              <recommended-contractors-section
                v-if="loaded && canRecommendContractorsV2"
                :numbered-sections="numberedSectionsWithObservations"
              />
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
      <v-layout v-else justify-center class="pdf-background">
        <div class="loading">
          <div>
            <div class="fa fa-refresh rotating" />
            Loading your report
          </div>
        </div>

        <v-flex xs11 sm8 md6 class="pdf-inner">
          <div class="fake-pdf">
            <div class="section">
              <div class="section-header"> Risus viverra adipiscing </div>
              <div class="section-body">
                Lorem ipsum dolor sit amet consectetur adipiscing elit.
                Tristique et egestas quis ipsum suspendisse ultrices gravida
                dictum. Vitae aliquet nec ullamcorper sit amet risus nullam
                eget. Cras tincidunt lobortis feugiat vivamus at. Duis ultricies
                lacus sed turpis tincidunt id aliquet. Tortor vitae purus
                faucibus ornare suspendisse sed nisi lacus sed. Sagittis aliquam
                malesuada bibendum arcu vitae elementum curabitur vitae nunc.
                Morbi non arcu risus quis varius quam quisque id.
              </div>
            </div>
            <div class="section">
              <div class="section-header"> Lorem ipsum dolor sit amet </div>
              <div class="section-body">
                <div class="row">
                  <strong>1.</strong> sed do eiusmod tempor incididunt ut labore
                </div>
                <div class="row">
                  <strong>2.</strong> Lorem ipsum dolor sit amet consectetur
                  adipiscing elit
                </div>
                <div class="row">
                  <strong>3.</strong> Tristique et egestas quis ipsum
                  suspendisse ultrices gravida dictum.
                </div>
              </div>
            </div>
            <div class="section">
              <div class="section-header"> Nec feugiat nisl pretium </div>
              <div class="section-body">
                <p
                  >Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
                  do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                  Risus viverra adipiscing at in tellus integer feugiat. Nec
                  feugiat nisl pretium fusce id velit. Lorem ipsum dolor sit
                  amet consectetur adipiscing elit.</p
                >
                <p
                  >Risus viverra adipiscing at in tellus integer feugiat. Nec
                  feugiat nisl pretium fusce id velit. Lorem ipsum dolor sit
                  amet consectetur adipiscing elit.</p
                >
              </div>
            </div>
            <div class="footer-row">
              <div>
                <strong>Revision 1.2.3</strong>
              </div>
              <div> Page 1 of 1 </div>
            </div>
          </div>
        </v-flex>
      </v-layout>
      <toast />
      <transition name="fade-in">
        <overlay-photo v-if="hasOverlayPhoto" />
      </transition>
      <transition name="fade-in">
        <overlay-photos v-if="hasOverlayPhotos" />
      </transition>
      <template v-if="!isSampleReport">
        <quick-edit-observation
          v-if="editObservation"
          @change="updateEditedObservation"
          @close="unsetEdit('Observation')"
        />
        <location-builder
          v-if="editLocation"
          v-model="editObservation.attributes.location"
          :options1="locationOptions1"
          :options2="locationOptions2"
          :options3="locationOptions3"
          @close="unsetEdit('Location')"
        />
        <leave-review
          v-if="review"
          :reviewer="reviewer"  
          @close="review = false"
        />
        <post-publish-prompt
          v-if="postPublishPrompt"
          @close="postPublishPrompt = false"
        />
        <publish-report
          v-if="publishReport === 'automations'"
          @close="publishReport = false"
          @change="updatePublished"
        />
        <publish-report-actions
          v-if="publishReport === 'actions'"
          @close="publishReport = false"
          @change="updatePublished"
        />
        <publish-isn
          v-if="publishReport === 'isn' && isIsn"
          :reportId="report.id"
          @close="publishReport = false"
          @change="updatePublished"
        />
        <submit-report
          v-if="submitReport"
          @close="submitReport = false"
          @change="updateSubmission"
        />
      </template>
    </v-container>
  </v-app>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import axios from '../../AxiosService'
import DefectCard from './DefectCard'
import DefectOptions from './DefectOptions'
import LeaveReview from './modals/LeaveReview/LeaveReview'
import LocationBuilder from '../template_editor/modal/LocationBuilder'
import OverlayPhoto from './OverlayPhoto'
import OverlayPhotos from './OverlayPhotos'
import QuickEditObservation from './modals/QuickEditObservation'
import ReportHeader from './ReportHeader'
import ReportRecommendedContractors from './ReportRecommendedContractors.vue'
import RecommendedContractorsSection from '../recommended_contractors/RecommendedContractorsSection.vue'
import RIB from './RIB'
import RRB from './RRB'
import SectionTabs from './SectionTabs'
import SidebarMenu from './SidebarMenu'
import Stats from './Stats'
import Toast from '../notifier/Toast'
import PostPublishPrompt from './modals/PostPublishPrompt'
import PublishIsn from './modals/PublishIsn'
import PublishReport from './modals/PublishReport'
import PublishReportActions from './modals/PublishReportActions'
import SubmitReport from './modals/SubmitReport'

export default {
  beforeCreate() {
    document.getElementsByTagName('html')[0].classList.add('hide-overflow')
  },
  components: {
    DefectCard,
    DefectOptions,
    LeaveReview,
    LocationBuilder,
    OverlayPhoto,
    OverlayPhotos,
    QuickEditObservation,
    RIB,
    RRB,
    ReportHeader,
    ReportRecommendedContractors,
    RecommendedContractorsSection,
    SidebarMenu,
    SectionTabs,
    Stats,
    Toast,
    PostPublishPrompt,
    PublishIsn,
    PublishReport,
    PublishReportActions,
    SubmitReport,
  },
  computed: {
    ...mapState('reports', [
      'activeFilter',
      'activeSection',
      'company',
      'defectCounts',
      'editLocation',
      'editObservation',
      'inspection',
      'items',
      'loggedIn',
      'observations',
      'overlayPhoto',
      'overlayPhotos',
      'sampleReport',
      'sections',
      'report',
      'reportContractors',
      'reportView',
      'recommendedContractors',
      'rib',
      'rrb',
      'userProfile',
    ]),
    canEdit() {
      return (
        this.loggedIn &&
        !this.isSampleReport &&
        !this.customView &&
        ((['inspector', 'manager'].includes(this.userProfile.type) &&
          this.userProfile.attributes.company_id ===
            this.company.attributes.id) ||
          this.userProfile.type === 'admin')
      )
    },
    canRefresh() {
      return this.loggedIn && !this.isSampleReport
    },
    canRecommendContractors() {
      return (
        !this.recommendedContractorsV2 &&
        this.reportContractors.length > 0 &&
        this.company.attributes.settings.enable_recommended_contractors &&
        this.report.attributes.settings.display_recommendations &&
        !this.viewingRestricted &&
        !this.report.attributes.not_show_recommended_contractors_on_reports
      )
    },
    canRecommendContractorsV2() {
      return (
        this.recommendedContractorsV2 &&
        this.company.attributes.settings.enable_recommended_contractors &&
        this.report.attributes.settings.display_recommendations &&
        !this.viewingRestricted &&
        !this.report.attributes.not_show_recommended_contractors_on_reports &&
        (this.reportContractors.length > 0 ||
          this.company.attributes.settings.use_angi_network_contractors)
      )
    },
    categoryKeys() {
      return {
        '-1': 'category_low',
        0: 'category_med',
        1: 'category_high',
        2: 'category_good',
        3: 'category_partial',
        4: 'category_bad',
      }
    },
    customView() {
      return this.rrb || this.rib
    },
    displayAll() {
      return this.activeFilter === 'full'
    },
    displayHazards() {
      return this.activeFilter === 'hazards'
    },
    displayReportText() {
      return (
        (this.report.attributes.settings.full_report_text || '').length > 0 &&
        this.displayAll &&
        !this.customView
      )
    },
    displayRH() {
      return this.activeFilter === 'rh'
    },
    displaySummary() {
      return (
        this.report.attributes.settings.display_summary &&
        this.displayAll &&
        !this.customView
      )
    },
    hasOverlayPhoto() {
      return this.overlayPhoto
    },
    hasOverlayPhotos() {
      return this.overlayPhotos.length > 0
    },
    isAdmin() {
      return this.userProfile.type === 'admin'
    },
    isIsn() {
      return this.inspection.attributes.isn_id
    },
    isSampleReport() {
      return (
        this.$route.name === 'sample-report' ||
        (this.$route.query.sample_id || '').length > 0
      )
    },
    locationOptions1() {
      return this.company.attributes.settings.location_options_1.split(';')
    },
    locationOptions2() {
      return this.company.attributes.settings.location_options_2.split(';')
    },
    locationOptions3() {
      return this.company.attributes.settings.location_options_3.split(';')
    },
    numberedSectionsWithObservations() {
      return this.numberedSections.filter((s) => {
        return s.observations.length > 0
      })
    },
    pdfReport() {
      return this.report.attributes.pdf_only
    },
    pdfsBeingRefreshed() {
      if (this.reinspection) {
        return !!this.report.attributes.pdf_started_at && this.poller
      } else {
        return (
          (!!this.report.attributes.pdf_started_at ||
            !!this.report.attributes.summary_pdf_started_at) &&
          this.poller
        )
      }
    },
    pdfGeneratorUrl() {
      let head = 'https://pdf'
      const separateEnvs = ['development', 'staging']
      separateEnvs.map((env) => {
        if (process.env.RAILS_ENV === env) {
          head += `-${env}`
        }
      })
      return `${head}.spectora.com`
    },
    pdfUrl() {
      if (this.firestore) {
        if (this.canEdit) {
          return `${this.pdfGeneratorUrl}/${this.$route.params.id}?ignoreClientComplete=true`
        }

        return `${this.pdfGeneratorUrl}/${this.$route.params.id}`
      }

      if (this.isSampleReport) {
        return this.sampleReport ? this.sampleReport.attributes.pdf_url : null
      } else {
        return this.report.attributes.pdf_url
      }
    },
    recommendedContractorsV2() {
      return this.company.attributes.settings.recommended_contractors_v2
    },
    reinspection() {
      return !!this.report.attributes.settings.report_view_id
    },
    reportTitle() {
      if (!this.loaded) {
        return ''
      } else if (this.sampleReport) {
        return `1234 Main Street | ${this.company.attributes.slug || ''}`
      } else {
        return `${
          this.inspection.attributes.property_address || 'Spectora Report'
        } | ${this.company.attributes.slug || ''}`
      }
    },
    reviews() {
      return this.inspection.inspection_reviews || []
    },
    reviewModal() {
      return (
        this.company.attributes.settings.enable_inspection_reviews &&
        this.company.attributes.settings.inspection_reviews_report_popup
      )
    },
    summaryCategories() {
      let cats = []
      if (
        this.report.attributes.settings.category_high_in_summary &&
        this.report.attributes.settings.categories > 1
      )
        cats.push(1)
      if (this.report.attributes.settings.category_low_in_summary) cats.push(-1)
      if (this.report.attributes.settings.category_med_in_summary) cats.push(0)

      return cats
    },
    summaryUrl() {
      if (this.firestore) {
        if (this.canEdit) {
          return `${this.pdfGeneratorUrl}/${this.$route.params.id}?ignoreClientComplete=true&summary=true`
        }

        return `${this.pdfGeneratorUrl}/${this.$route.params.id}?summary=true`
      }

      if (this.isSampleReport) {
        return this.sampleReport
          ? this.sampleReport.attributes.summary_pdf_url
          : null
      } else {
        return this.report.attributes.summary_url
      }
    },
    viewingRestricted() {
      return this.report.attributes.viewing_restricted
    },
  },
  data() {
    return {
      displaySidebar: false,
      firestore: false,
      hasCheckedForUpdates: false,
      loaded: false,
      loadingPdf: false,
      numberedSections: [],
      poller: null,
      postPublishPrompt: false,
      publishReport: false,
      review: false,
      reviewer: null,
      scrollHeader: false,
      showPdfOptions: false,
      submitReport: false,
    }
  },
  metaInfo() {
    return {
      title: this.reportTitle,
    }
  },
  methods: {
    ...mapActions('reports', ['fetchReportFirestore', 'updateReportPayload']),
    addItemNumbers(items) {
      let counter = 0
      items.map((item) => {
        if (!item.attributes.is_overview) {
          item.number = counter + 1
          counter += 1
        }
      })
      return items
    },
    buildNumberedSections() {
      if (this.firestore) {
        this.numberedSections = this.sections.map((section, i) => {
          const items = this.items.filter(
            (x) => x.attributes.report_section_id === section.attributes.id
          )
          this.addItemNumbers(items)
          const observations = this.observations.filter(
            (x) => x.attributes.report_section_id === section.attributes.id
          )
          observations.map((observation) => {
            observation.attributes.report_item = items.find(
              (item) => item.id === observation.attributes.report_item_id
            )
          })
          return {
            number: i + 1,
            section,
            items,
            observations,
            observationsLoaded: true,
          }
        })
      } else {
        this.numberedSections = this.sections.map((s, i) => {
          return {
            number: i + 1,
            section: s,
            observations: [],
            observationsLoaded: false,
          }
        })
      }
    },
    checkPdfStatus() {
      this.$store.dispatch('reports/fetchPdfStatus')
    },
    defectObservations(numberedSection) {
      let dOs = []
      numberedSection.items.forEach((i) => {
        dOs = dOs.concat(
          numberedSection.observations
            .filter(
              (o) =>
                o.attributes.report_item_id === i.id &&
                o.attributes.comment_type === 'defect' &&
                !o.attributes.report_item.attributes.is_overview
            )
            .map((o, idx) => {
              o.number = `${numberedSection.number}.${
                o.attributes.report_item.number
              }.${idx + 1}`
              return o
            })
        )
      })

      if (this.activeFilter === 'summary') {
        dOs = dOs.sort((a, b) => {
          return b.attributes.category - a.attributes.category
        })
      }

      return dOs
    },
    displayable(category) {
      if (this.displayHazards) {
        return category === 1
      } else if (this.displayRH) {
        return [0, 1].includes(category)
      } else if (this.activeFilter === 'summary') {
        return this.summaryCategories.includes(category)
      } else if (this.reinspection) {
        if (this.activeFilter === 'resolved' && category !== 2) return false
        if (this.activeFilter === 'unsatisfactory' && category !== 3)
          return false
        if (this.activeFilter === 'notResolved' && category !== 4) return false
        return true
      } else {
        return true
      }
    },
    fetchAdditional() {
      return this.$store.dispatch('reports/fetchAccount').then(() => {
        let query

        if (this.firestore) {
          query = Promise.resolve(this.report)
        } else {
          if (this.isSampleReport) {
            query = this.$store.dispatch(
              'reports/fetchSampleReport',
              this.$route.query.sample_id
            )
          } else {
            query = this.$store.dispatch(
              'reports/fetchReport',
              this.$route.params.id
            )
          }
        }

        return query.then((report) => {
          if (this.$route.query.id_token && !this.userProfile.id)
            this.$store.dispatch(
              'reports/fetchViewer',
              this.$route.query.id_token
            )
          if (report.attributes.is_firestore) {
            this.firestore = true
          }
          if (!this.firestore && this.pdfReport) {
            this.preparePdfReport()
          } else {
            if (this.$route.query.report_view) {
              this.$store.dispatch(
                'reports/fetchReportView',
                this.$route.query.report_view
              )
            } else if (this.reinspection) {
              this.$store.dispatch(
                'reports/fetchReportView',
                this.report.attributes.settings.report_view_id
              )
            }
          }
        })
      })
    },
    fetchRecommendedContractors() {
      this.$store.dispatch('reports/fetchReportContractors', this.report.id)
    },
    async fetchData() {
      this.firestore = await this.fetchDataFirestore()
      if (this.firestore) {
        this.buildNumberedSections()
        this.$store.dispatch('reports/setDefectCounts')
        await this.fetchAdditional()
        this.handleRRB()
      } else {
        await this.fetchAdditional()
        await this.fetchRecommendedContractors()
        await this.fetchDataRails()
      }
      await this.$store.dispatch('reports/fetchInspection')
      await this.$store
        .dispatch('reports/fetchRecommendations')
        .then(() => {
          if (
            !this.loggedIn ||
            this.isAdmin ||
            (this.loggedIn && !this.userProfile.attributes.company_id)
          ) {
            this.$store
              .dispatch(
                'reports/fetchCompany',
                this.inspection.attributes.company_id
              )
              .then(() => {
                this.scheduleReview()
              })
          }
        })
        .finally(() => {
          this.loaded = true
          this.setupScroller()
        })
      if (this.firestore && !this.hasCheckedForUpdates) {
        this.hasCheckedForUpdates = true
        const hasUpdate = await this.updateReportPayload(this.report.id)
        if (hasUpdate) {
          this.fetchData()
        }
      }
    },
    fetchDataFirestore() {
      return this.fetchReportFirestore(this.$route.params.id)
    },
    fetchDataRails() {
      return this.$store.dispatch('reports/fetchSections').then(() => {
        this.buildNumberedSections()

        let observationReqs = this.numberedSections.map((s) => {
          let params = {
            report_id: this.report.id,
            report_section_id: s.section.id,
            has_data: true,
            include: 'observation_photos',
            is_sample: this.isSampleReport,
          }
          return this.$store
            .dispatch('reports/fetchObservations', params)
            .then((obs) => {
              s.items = this.items.filter((i) => {
                return i.attributes.report_section_id === s.section.id
              })
              let counter = 0
              s.items.forEach((i) => {
                if (!i.attributes.is_overview) {
                  i.number = counter + 1
                  counter += 1
                }
              })

              obs.forEach((o) => {
                o.attributes.report_item = s.items.find((i) => {
                  return i.id === o.attributes.report_item_id
                })
              })

              s.observations = obs
              s.observationsLoaded = true
            })
        })

        return Promise.all(observationReqs).then(() => {
          this.$store.dispatch('reports/setDefectCounts')
          this.handleRRB()
        })
      })
    },
    firstItemId(sectionId) {
      return (
        this.items.find((i) => {
          return sectionId === i.attributes.report_section_id
        }) || {}
      ).id
    },
    handleRRB() {
      if (this.$route.query.start_rrb) {
        this.$store.commit('reports/setValue', { key: 'rrb', value: true })
      }
      if (this.$route.query.report_view) {
        if (this.$route.query.reinspection) {
          this.$store.commit('reports/setValue', { key: 'rib', value: true })
        } else {
          this.$store.commit('reports/setValue', { key: 'rrb', value: true })
        }
        this.$store.commit('reports/setValue', {
          key: 'activeFilter',
          value: 'all',
        })
      } else {
        if (this.$route.query.reinspection) {
          this.$store.commit('reports/setValue', { key: 'rib', value: true })
          this.$store.commit('reports/setValue', {
            key: 'activeFilter',
            value: 'all',
          })
        }
      }
    },
    handleScroll(e) {
      if (this.customView) return

      const scroller = document.getElementById('scrollable')
      let position = scroller.scrollTop + 50
      let newActive = this.numberedSections.find((s, i) => {
        if (this.numberedSections[i + 1]) {
          let el1 = document.getElementById(`section-${s.section.id}`)
          let el2 = document.getElementById(
            `section-${this.numberedSections[i + 1].section.id}`
          )

          if (el1 && el2) {
            return position > el1.offsetTop && position < el2.offsetTop
          } else {
            return null
          }
        } else {
          return null
        }
      })

      let newActiveId = newActive ? newActive.section.id : 'summary'
      if (this.activeSection !== newActiveId)
        this.$store.commit('reports/setValue', {
          key: 'activeSectionId',
          value: newActiveId,
        })
    },
    incrementReportCategory(oldCategory, newCategory) {
      let stats = JSON.parse(JSON.stringify(this.report.attributes.stats))
      stats[this.categoryKeys[oldCategory]] -= 1
      if (newCategory) stats[this.categoryKeys[newCategory]] += 1
      let report = JSON.parse(JSON.stringify(this.report))
      report.attributes.stats = stats
      this.$store.commit('reports/setValue', { key: 'report', value: report })
    },
    obsLabel(observationId) {
      if (!this.reportView) return ''
      let entry = this.reportView.attributes.whitelist.find(
        (w) => observationId === w.observation_id
      )
      return entry ? entry.number : ''
    },
    optionsScroller(e) {
      let options = document.getElementById('options-row')
      let scroller = document.getElementById('scrollable')
      if (options) {
        if (options.offsetTop <= scroller.scrollTop) {
          if (!options.classList.value.includes('scroll')) {
            this.scrollHeader = true
          } else {
            let header = document.getElementById('header')
            if (scroller.scrollTop <= header.offsetHeight)
              this.scrollHeader = false
          }
        } else {
          this.scrollHeader = false
        }
      }
    },
    preparePdfReport() {
      this.loadingPdf = true
      if (!this.report.attributes.pdf_started_at) {
        let ctx = this
        setTimeout(() => {
          window.location.href = ctx.report.attributes.pdf_url
        }, 1000)
      }
    },
    refreshPdfs() {
      this.$store.dispatch('reports/updatePdfs')
    },
    scheduleReview() {

      if (
        !this.loggedIn &&
        this.$route.query.id_token &&
        this.inspection.attributes.published_at
      ) {
        if (!this.reviewModal) return
        axios
          .get(
            `/api/v1/public/inspections/${this.inspection.attributes.slug}/reviewers/${this.$route.query.id_token}`
          )
          .then((response) => {
            this.reviewer = response.data.data
            
            let reviewerType =
              this.reviewer.type === 'agent' ? 'Agent' : 'Buyer'
            if (
              !this.reviews.find(
                (r) =>
                  r.attributes.reviewer_id === this.reviewer.attributes.id &&
                  r.attributes.reviewer_type === reviewerType
              )
            ) {
              setTimeout(() => {
                this.review = true
              }, 5000)
            }
          })
      }
    },
    setReportPublish(type) {
      this.publishReport = type
    },
    unsetEdit(type) {
      this.$store.commit('reports/setValue', {
        key: `edit${type}`,
        value: null,
      })
    },
    updateEditedObservation(data) {
      const updatables = [
        'name',
        'text',
        'flagged',
        'location',
        'category',
        'recommendation_id',
        'display_estimate',
        'estimate_min',
        'estimate_max',
        'value',
        'value2',
        'unit_type_choices',
        'unit_type',
        'choices',
      ]
      let item = this.items.find((i) => {
        return i.id === data.attributes.report_item_id
      })
      this.numberedSections.forEach((s) => {
        if (s.section.id === item.attributes.report_section_id) {
          s.observations.forEach((o) => {
            if (o.id === data.id) {
              let existingCategory = o.attributes.category
              let newCategory = data.attributes.category
              updatables.forEach((att) => {
                o.attributes[att] = data.attributes[att]
              })

              if (existingCategory !== newCategory)
                this.incrementReportCategory(existingCategory, newCategory)
            }
          })

          if (!data.attributes.value && !data.attributes.value2) {
            s.observations = s.observations.filter((o) => o.id !== data.id)
            this.incrementReportCategory(data.attributes.category, null)
            this.$store.dispatch('reports/removeObservation', data.id)
          }
        }
      })
    },
    updateEditObservationLocation(val) {
      this.editObservation.attributes.location = val
    },
    updatePublished(atts) {
      this.report.attributes.published = true
      this.postPublishPrompt = true
    },
    updateSubmission(submissionDate) {
      this.report.attributes.submitted_on_formatted = submissionDate
    },
    setActiveFilter(filter) {
      this.$store.commit('reports/setValue', {
        key: 'activeFilter',
        value: filter,
      })
    },
    setupScroller() {
      document
        .getElementById('scrollable')
        .addEventListener('scroll', this.handleScroll)
      document
        .getElementById('scrollable')
        .addEventListener('scroll', this.optionsScroller)
    },
    checkSummaryParam() {
      try {
        const queryString = window.location.search
        const urlParams = new URLSearchParams(queryString)
        const summary = urlParams.get('summary')

        if (summary === 'true') {
          this.setActiveFilter('summary')
        }
      } catch (e) {
        console.log('Error on checkSummaryParam: ', e)
      }
    },
  },
  async mounted() {
    await this.fetchData()
    this.checkSummaryParam()
  },
  watch: {
    report: {
      handler: function (newV, oldV) {
        if (!!newV.attributes.pdf_started_at && !this.poller) {
          this.poller = setInterval(this.checkPdfStatus, 7500)
        } else if (!newV.attributes.pdf_started_at && this.poller) {
          clearInterval(this.poller)
          this.poller = null
          if (this.pdfReport) this.preparePdfReport()
        }
      },
      deep: true,
    },
  },
}
</script>

<style lang="scss" scoped>
#report .container {
  margin: 0;
  width: 100%;
  max-width: 100%;
}

.sidebar-container {
  padding: 1em 0;
  min-height: 100vh;
  max-height: 100vh;
  background-color: #fff;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12);
  overflow-y: auto;
}

.main-container {
  height: 100vh;
  max-height: 100vh;
  overflow-x: scroll;
}

.main {
  justify-content: center;
}

.report-header-placeholder {
  height: 30em;
  background-color: #3b464f;
}

.pdf-btn {
  color: #dfe6f6;
  height: 3em;
  line-height: 2em;
  padding: 0.5em 1.5em;
  font-size: 0.75em;
  background-color: #45769c;
  cursor: pointer;

  &.refreshing {
    color: #888;
    background-color: #dfdfdf;
  }
}

.report-body {
  transition: all 0.5s linear;
  background-color: inherit;
}

.report-text,
.summary-text {
  margin-top: 2em;
}

.section-header {
  font-size: 2em;
  font-weight: 300;
  margin: 1em 0 0.5em 0;
  justify-content: space-between;
  display: flex;

  .section-icon {
    vertical-align: middle;
    display: inline-block;
    height: 1em;
    width: 1em;
    margin-right: 0.25em;
    opacity: 0.3;
  }

  a {
    align-self: center;
  }
}

.ratings-legend {
  text-align: right;
  span {
    margin-left: 1em;
  }
}

.observations {
  .observation-container {
    padding: 0.5em 0;
    position: relative;
    min-height: 30em;

    &:nth-child(3n) {
      padding-left: 1em;
      padding-right: 0;
    }
    &:nth-child(3n + 1) {
      padding-right: 1em;
    }
    &:nth-child(3n - 1) {
      padding-left: 0.5em;
      padding-right: 0.5em;
    }
  }
}

.loading-wrapper {
  position: relative;
  overflow: hidden;

  .swoosh {
    position: absolute;
    height: 40em;
    content: ' ';
    width: 2em;
    transform: rotate(15deg);
    background-color: #e8e8e8;
    box-shadow: 1px 1px 2em 2em #e8e8e8;
    animation: left-to-right 1.5s ease-out infinite;
  }
}

.observation-container.fake {
  opacity: 0.3;

  .defect-card {
    height: 30em;
    border-radius: 2px;
    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16),
      0 2px 10px 0 rgba(0, 0, 0, 0.12);
    filter: blur(2px);

    .defect-header {
      height: 2.75em;
      display: flex;
      padding: 0 1em;
      align-items: center;
      background-color: #888;
      color: #fff;
    }

    .defect-body {
      height: 27.25em;
      padding: 1em;
      background-color: #fff;

      .name {
        color: #555;
        font-size: 1.25em;
      }

      .text {
        color: #999;
      }
    }
  }
}

.pdf-background {
  height: 100vh;
  position: relative;
  background-color: rgba(0, 0, 0, 0.75);

  .loading {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    font-size: 2em;
    color: rgba(0, 0, 0, 0.75);
    .fa {
      margin-right: 0.5em;
    }
  }

  .pdf-inner {
    background-color: #fafafa;
    box-shadow: inset 0px 0px 20px 0px #a8a8a8;

    .fake-pdf {
      padding: 1em 2em;
      opacity: 0.5;
      filter: blur(4px);

      .section-header {
        border: 1px solid #000;
        padding: 0.5em;
      }

      .row {
        margin-left: 2em;
      }

      .footer-row {
        display: flex;
        justify-content: space-between;
      }
    }
  }
}

@keyframes left-to-right {
  0% {
    left: -10%;
  }
  100% {
    left: 110%;
  }
}

@keyframes slide-in {
  from {
    left: -100%;
  }
  to {
    left: 0;
  }
}

@keyframes slide-out {
  from {
    left: 0;
  }
  to {
    left: -100%;
  }
}

.slide-down-leave-active {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 4;
  transition: 0.5s;
}
.slide-down-enter-active {
  transition: 0.5s;
}

.slide-down-enter {
  opacity: 0;
  transform: translate(0, -100%);
}

.slide-down-enter-to,
.slide-down-leave {
  opacity: 1;
  transform: translate(0);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translate(0, -100%);
}

.fade-in-leave-active,
.fade-in-enter-active {
  transition: 0.25s;
}

.fade-in-enter {
  opacity: 0;
}

.fade-in-enter-to,
.fade-in-leave {
  opacity: 1;
}

.fade-in-leave-to {
  opacity: 0;
}

@media (min-width: 1264px) {
  .sidebar-container {
    &.flex.lg2-5 {
      flex-basis: 21%;
      flex-grow: 0;
      max-width: 21%;
    }
  }

  .main-container {
    &.flex.lg9-5 {
      flex-basis: 79%;
      flex-grow: 0;
      max-width: 79%;
    }
  }
}

@media (max-width: 1264px) {
  .observations .observation-container:nth-child(2n) {
    padding-left: 0.5em;
    padding-right: 0;
  }
  .observations .observation-container:nth-child(2n - 1) {
    padding-right: 0.5em;
    padding-left: 0;
  }
}

@media (max-width: 960px) {
  .section-header {
    flex-wrap: wrap;
    .section-name {
      width: 100%;
      margin-bottom: 0.5em;
      border-bottom: 2px solid #e8e8e8;
    }
    a {
      margin-left: auto;
    }
  }

  .sidebar-container {
    position: absolute;
    top: 0;
    z-index: 1;
    display: none;
    &.display {
      display: block;
      animation: slide-in 0.5s ease-out forwards;
    }
    &.remove {
      display: block;
      animation: slide-out 0.5s ease-out forwards;
    }
  }

  .observation-container.fake {
    .defect-card {
      .defect-header {
        min-height: 4em;
        max-height: 4em;
      }
      .defect-body {
        height: 26em;
      }
    }
  }
}

@media (max-width: 600px) {
  .observations .observation-container:nth-child(n) {
    padding-left: 0;
    padding-right: 0;
  }
}
</style>

<style lang="scss">
#report {
  .report-text {
    ul {
      margin-bottom: 1em;
    }
    li {
      list-style: disc inside;
    }
  }

  .defect-card,
  .tab-observation {
    .quick-edit {
      display: none;
      position: absolute;
      margin: 0;
      top: 1em;
      right: 1em;
      background-color: #fff;
      color: #888;
      border: 1px solid #888;
      box-shadow: none;
      transition: all 0.1s linear;
      font-size: 0.75em;
      padding: 0.5em 1em;
      line-height: 1.75em;
      align-self: flex-start;

      .fa {
        color: inherit;
      }

      &:hover {
        border-color: #5c9ccf;
        color: #5c9ccf;
      }
    }
  }

  .report-option {
    color: #45769c;

    .v-list__tile__title {
      height: auto;

      a {
        color: inherit;
      }

      .icon-container {
        display: inline-block;
        margin-right: 0.25em;
      }
    }
  }

  .options-row {
    background-color: #3b464f;
    height: 3.5em;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 2em;

    .sidebar-option {
      display: none;
    }

    &.scroll {
      position: fixed;
      top: 0;
      right: 0;
      width: 79%;
      z-index: 3;
    }
  }

  .options {
    display: flex;
    .option {
      height: 3em;
      line-height: 2em;
      cursor: pointer;
      color: #fff;
      background-color: #45769c;
      color: #dfe6f6;
      padding: 0.5em 1em;
      font-size: 0.75em;
      display: inline-block;
      transition: background 0.1s linear;
      border-right: 1px solid #3b464f;

      &.active {
        color: #fff;
        background-color: #5c9ccf;
      }

      &:hover {
        background-color: #5c9ccf;
      }

      &:first-child {
        border-radius: 2px 0 0 2px;
      }
      &:last-child {
        border-radius: 0 2px 0 2px;
        border-right: none;
      }
    }
  }

  .info-tooltip {
    color: #fff;
    background-color: #65c635;
    font-size: 1em;
    max-width: 20em;
    text-align: center;
  }

  &.sample-report {
    .defect-card,
    .tab-observation {
      .quick-edit {
        visibility: hidden;
      }
    }
  }

  .observations {
    .observation-container {
      &:nth-child(3n) {
        .defect-options .select {
          left: -0.25em;
        }
      }
      &:nth-child(3n + 1) {
        .defect-options .select {
          left: -1.25em;
        }
      }
      &:nth-child(3n - 1) {
        .defect-options .select {
          left: -0.75em;
        }
      }
    }
  }
}

.firestore-not-saved {
  background-color: #5c9ccf;
  border-radius: 0.5rem;
  padding: 1rem 2rem;
  position: relative;
  top: 1rem;
}

@media (max-width: 1264px) {
  #report {
    .options-row.scroll {
      width: 66.66%;
    }

    .observations .observation-container:nth-child(2n) {
      .defect-options .select {
        left: -0.75em;
      }
    }
    .observations .observation-container:nth-child(2n - 1) {
      .defect-options .select {
        left: -1.25em;
      }
    }
  }
}

@media (max-height: 768px) and (min-width: 1024px) {
  #report {
    .sidebar-container {
      &.flex.lg2-5 {
        flex-basis: 22.5%;
        flex-grow: 0;
        max-width: 22.5%;
      }

      .sidebar-top {
        padding-left: 1em;
        padding-right: 1em;
      }
    }

    .main-container {
      &.flex.lg9-5 {
        flex-basis: 77.5%;
        flex-grow: 0;
        max-width: 77.5%;
      }
    }

    .options-row.scroll {
      width: 77.5%;
    }
  }
}

@media (max-width: 960px) {
  #report {
    .v--modal-box.v--modal {
      width: 95% !important;
      left: 2.5% !important;
    }

    .options-row.scroll {
      width: 100%;
    }
    .options {
      font-size: 16px;
    }
    .options .option {
      width: 4em;
      text-align: center;
    }
    .options-row .sidebar-option {
      display: inline-block;
    }

    .pdf-btn span,
    .option span {
      display: none;
    }
  }
}

@media (max-width: 600px) {
  #report .observations .observation-container:nth-child(n) {
    .defect-options .select {
      left: -1em;
    }
  }
}
</style>
